/**
 * Contact enums as runtime values.
 *
 * Declared here rather than imported from @prisma/client: that package breaks
 * the browser bundle, and the generated GraphQL types are types only — they
 * vanish at build time, so a status cannot be named in a query variable or
 * compared against a row without something like this.
 */
export enum ContactStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  ARCHIVED = 'ARCHIVED',
}

/**
 * How each status reads on screen.
 *
 * Every status other than ACTIVE is out of the running — the enrichment path
 * compares against ACTIVE and nothing else — but they are not the same event,
 * so they are not given the same word.
 */
export const ContactStatusLabels: Record<ContactStatus, string> = {
  [ContactStatus.ACTIVE]: 'Active',
  [ContactStatus.INACTIVE]: 'Inactive',
  [ContactStatus.ARCHIVED]: 'Archived',
}

/** Every status a contact can hold once it is out of the running. */
export const OUT_OF_SERVICE_CONTACT_STATUSES = [
  ContactStatus.INACTIVE,
  ContactStatus.ARCHIVED,
]

/** Where a directory contact came from. Mirrors the Prisma `ContactSource` enum. */
export enum ContactSource {
  EMPLOYMENT_EXPERIENCE = 'EMPLOYMENT_EXPERIENCE',
  DRIVER_APPLICATION = 'DRIVER_APPLICATION',
  VERIFICATION_OF_EMPLOYMENT = 'VERIFICATION_OF_EMPLOYMENT',
  VERIFICATION_OF_EMPLOYMENT_EMAIL = 'VERIFICATION_OF_EMPLOYMENT_EMAIL',
  VOE_AUDIT_TRAIL = 'VOE_AUDIT_TRAIL',
  VOE_RESPONSE = 'VOE_RESPONSE',
  MANUAL = 'MANUAL',
  IMPORTED = 'IMPORTED',
  HUBSPOT = 'HUBSPOT',
  FMCSA_CENSUS = 'FMCSA_CENSUS',
}

/**
 * How each source reads on screen, everywhere a recruiter sees a contact:
 * the picker, the contacts block in the employment drawer, the table.
 *
 * One label per source, and every source has one. The picker used to badge
 * four of the nine and leave the rest bare, which read as "no idea where this
 * came from" for contacts we had in fact reached.
 */
export const ContactSourceLabels: Record<
  ContactSource,
  { label: string; tooltip: string }
> = {
  [ContactSource.VOE_RESPONSE]: {
    label: 'Answered a VoE',
    tooltip: 'The employer submitted this on a verification response form',
  },
  [ContactSource.VOE_AUDIT_TRAIL]: {
    label: 'Reached before',
    tooltip: 'Recorded on a logged verification call or outreach',
  },
  [ContactSource.VERIFICATION_OF_EMPLOYMENT]: {
    label: 'From a verification',
    tooltip: 'Entered on a verification of employment request',
  },
  [ContactSource.VERIFICATION_OF_EMPLOYMENT_EMAIL]: {
    label: 'From a verification',
    tooltip: 'The address a verification of employment was sent to',
  },
  [ContactSource.DRIVER_APPLICATION]: {
    label: 'From an application',
    tooltip: "From a driver's job application",
  },
  [ContactSource.EMPLOYMENT_EXPERIENCE]: {
    label: 'From employment history',
    tooltip: "From a driver's employment history",
  },
  [ContactSource.MANUAL]: {
    label: 'Added manually',
    tooltip: 'Added by hand on the carrier directory',
  },
  [ContactSource.IMPORTED]: {
    label: 'Imported',
    tooltip: 'Imported with a client data migration',
  },
  [ContactSource.HUBSPOT]: {
    label: 'From HubSpot',
    tooltip: 'Synced from HubSpot',
  },
  [ContactSource.FMCSA_CENSUS]: {
    label: 'From FMCSA filing',
    tooltip: "The contact on the carrier's MCS-150 registration with FMCSA",
  },
}

/** The label for a source string off the wire; null when unknown or absent. */
export const contactSourceLabel = (
  source?: string | null
): { label: string; tooltip: string } | null =>
  source && source in ContactSourceLabels
    ? ContactSourceLabels[source as ContactSource]
    : null
